import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { Row } from '@tanstack/table-core';
import { AssetRow, AssetType } from '@/app/assets/components/table/hooks/types';
import { Table } from '@tanstack/react-table';
import { useSelectedAssetContext } from '@/app/assets/contexts/SelectedAssetContext';

export const useAssetTableNavigation = (table: Table<AssetRow>) => {
  const [focusedRow, setFocusedRow] = useState<Row<AssetRow> | undefined>();
  const { setSelectedAsset, selectedAsset } = useSelectedAssetContext();

  useEffect(() => {
    if (!selectedAsset && focusedRow) {
      return setFocusedRow(undefined);
    }
    if (focusedRow?.original.basicInfo.rowType === 'pipeline') {
      return setSelectedAsset(undefined);
    }
    setSelectedAsset(focusedRow ? focusedRow.original : undefined);
  }, [focusedRow, setSelectedAsset, selectedAsset]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!focusedRow) {
        return;
      }

      const rows = table.getRowModel().rows;

      if (e.key === 'Escape') {
        setFocusedRow(undefined);
      }

      if (e.key === 'ArrowUp') {
        setFocusedRow((prev) => {
          const previousIndex = rows.findIndex((r) => r.id === prev?.id);
          if (previousIndex < 0) {
            return prev;
          }

          const newIndex = previousIndex - 1;
          if (newIndex < 0) {
            return prev;
          }

          let newRow = rows[newIndex];
          if (newRow.original.basicInfo.rowType === 'pipeline') {
            newRow = rows[newIndex - 1];
          }

          return newRow;
        });
      }

      if (e.key === 'ArrowDown') {
        setFocusedRow((prev) => {
          const previousIndex = rows.findIndex((r) => r.id === prev?.id);
          if (previousIndex < 0) {
            return prev;
          }

          const newIndex = previousIndex + 1;
          if (newIndex > rows.length - 1) {
            return prev;
          }

          let newRow = rows[newIndex];

          if (newRow.original.basicInfo.rowType === 'pipeline') {
            newRow = rows[newIndex + 1];
          }

          return newRow;
        });
      }
    },
    [focusedRow, table]
  );

  const onRowFocus = (row: Row<AssetRow> | undefined) => {
    const current = row?.original.basicInfo;

    const unfocusableRowTypes: AssetType[] = ['pendingOutput', 'pipeline'];
    if (current?.rowType && unfocusableRowTypes.includes(current?.rowType)) {
      return;
    }

    setFocusedRow((prev) => {
      const previous = prev?.original.basicInfo;

      if (previous?.id === current?.id && previous?.rowType === current?.rowType) {
        return undefined;
      }

      return row;
    });
    if (row) {
      setSelectedAsset(row.original);
    }
  };

  return {
    onKeyDown,
    setFocusedRow: onRowFocus,
    focusedRow,
  };
};
